import React, { FC, useState } from "react";
import toast from "react-hot-toast";
import DialogLayout from "../DialogLayout";
import Typography from "../../components/Typography/Typography";
import ASSETS from "../../assets";
import Button from "../../components/Button/Button";
import { authAPI, DeleteWhitelistUserRequest } from "../../services/api";

interface IProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  userEmail: string;
  onSuccess: () => void;
}

const DeleteWhitelistUser: FC<IProps> = ({ open, setOpen, userEmail, onSuccess }) => {
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    try {
      setLoading(true);
      
      const deleteData: DeleteWhitelistUserRequest = {
        email: userEmail,
      };

      const response = await authAPI.deleteWhitelistUser(deleteData);
      
      // Show success message
      toast.success(response.message || 'User removed from whitelist successfully!');
      
      // Close modal
      handleClose();
      
      // Refresh whitelist data
      onSuccess();
      
    } catch (err: any) {
      let errorMessage = 'Failed to remove user from whitelist. Please try again.';
      
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.status === 404) {
        errorMessage = 'User not found in whitelist.';
      } else if (err.response?.status === 400) {
        errorMessage = 'Invalid request. Please try again.';
      } else if (err.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <DialogLayout open={open}>
      <div className="w-full max-w-[536px] p-[25px] bg-white rounded-[15px] relative">
        <div
          className="absolute top-[25px] right-[25px] rounded-full size-[30px] bg-[#0000000D] cursor-pointer grid place-items-center"
          onClick={handleClose}
        >
          <img src={ASSETS.cross} alt="" />
        </div>
        <Typography variant="h2" className="font-bold title">
          Remove User from Whitelist
        </Typography>
        <Typography
          variant="body1"
          color="grey"
          className="mt-[5px] font-medium"
        >
          Are you sure you want to remove this user from the whitelist? This action cannot be undone.
        </Typography>
        
        <div className="border border-k-border rounded-lg p-6 mt-[25px] flex flex-col gap-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <Typography variant="body2" className="text-gray-600 mb-1">
              Email Address:
            </Typography>
            <Typography variant="body1" className="font-medium text-gray-800">
              {userEmail}
            </Typography>
          </div>
          
          <div className="flex gap-3">
            <Button
              label="Cancel"
              variant="outlined"
              className="flex-1 h-[45px]"
              onClick={handleClose}
              disabled={loading}
              type="button"
            />
            <Button
              label={loading ? "Removing..." : "Remove User"}
              variant="primary"
              className="flex-1 h-[45px] disabled:opacity-200 disabled:cursor-not-allowed disabled:!text-gray-400"
              onClick={handleDelete}
              loading={loading}
              disabled={loading}
              type="button"
            />
          </div>
        </div>
      </div>
    </DialogLayout>
  );
};

export default DeleteWhitelistUser;
